import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $getSelection, $insertNodes, $isRangeSelection, $isTextNode, type LexicalNode } from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useFindOrCreateTag, useTags } from "@/hooks/useTagQueries";
import { useTickerSymbols } from "@/hooks/useTickerQueries";
import { cn } from "@/lib/utils";
import { getTagColorClass, updateTagColorCache } from "@/services/TagService";
import { $createStockNode } from "../nodes/StockNode";
import { $createTagNode, $isTagNode, type TagNode } from "../nodes/TagNode";

interface Trigger {
  char: string;
  minLength: number;
}

interface SuggestionState {
  triggerChar: string | null;
  query: string;
  startIndex: number;
  endIndex: number;
  position: { left: number; top: number } | null;
  selectedIndex: number;
}

interface TagSuggestion {
  displayName: string;
  tagId: string;
  colorToken: string;
  isCreateOption?: boolean;
}

interface LexicalSuggestionsPluginProps {
  tagSuggestionsEnabled?: boolean;
  tickerSuggestionsEnabled?: boolean;
}

const TRIGGERS: Trigger[] = [
  { char: "#", minLength: 0 }, // #tag (支持立即显示，包括中文)
  { char: "$", minLength: 0 }, // $TICKER (支持立即显示)
];

export function LexicalSuggestionsPlugin({ tagSuggestionsEnabled = true, tickerSuggestionsEnabled = true }: LexicalSuggestionsPluginProps) {
  const [editor] = useLexicalComposerContext();
  const { currentUser } = useAuth();

  const [suggestionState, setSuggestionState] = useState<SuggestionState>({
    triggerChar: null,
    query: "",
    startIndex: 0,
    endIndex: 0,
    position: null,
    selectedIndex: 0,
  });

  // 使用 ref 存储最新的 suggestionState，避免闭包捕获旧值
  const suggestionStateRef = useRef(suggestionState);
  useEffect(() => {
    suggestionStateRef.current = suggestionState;
  }, [suggestionState]);

  // Ref for scrolling to selected item
  const suggestionsContainerRef = useRef<HTMLDivElement>(null);

  // Fetch tags and ticker symbols for suggestions (only when user is logged in)
  const { data: tags = [] } = useTags({
    enabled: !!currentUser && tagSuggestionsEnabled,
  });
  // v0.71.0: Use lightweight ListTickerSymbols endpoint (symbol only, no JOINs)
  // userTickersOnly = true: only show user's tickers in editor suggestions
  const { data: tickerSymbolsResponse } = useTickerSymbols({ userTickersOnly: true });
  const findOrCreateTagMutation = useFindOrCreateTag();

  const tickers = useMemo(() => {
    if (!tickerSymbolsResponse?.symbols) return [];
    return tickerSymbolsResponse.symbols.map((item) => item.symbol);
  }, [tickerSymbolsResponse]);

  const getSuggestions = useCallback(
    (triggerChar: string, query: string) => {
      if (triggerChar === "#") {
        const lowerQuery = query.toLowerCase();

        // Filter existing tags
        const existingTags = tags.filter((tag) => tag.displayName.toLowerCase().includes(lowerQuery)) as TagSuggestion[];

        // Check if exact match exists
        const exactMatchExists = tags.some((tag) => tag.displayName.toLowerCase() === lowerQuery);

        // Add "Create new tag" option if tag doesn't exist and user typed something
        if (!exactMatchExists && query.length > 0) {
          return [{ displayName: query, tagId: "", colorToken: "primary", isCreateOption: true }, ...existingTags];
        }

        return existingTags;
      }

      // Ticker suggestions
      const lowerQuery = query.toLowerCase();
      return tickers.filter((ticker) => ticker.toLowerCase().includes(lowerQuery));
    },
    [tags, tickers],
  );

  const hideSuggestions = useCallback(() => {
    setSuggestionState((prev) => ({
      ...prev,
      triggerChar: null,
      query: "",
      position: null,
      selectedIndex: 0,
    }));
  }, []);

  // 应用选择的建议
  const applySuggestion = useCallback(
    async (suggestion: string | TagSuggestion) => {
      // 使用 ref 获取最新的 suggestionState
      const state = suggestionStateRef.current;
      if (!state.triggerChar) return;

      editor.update(async () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return;
        }

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          return;
        }

        // 选择要替换的文本（通过修改 selection 的 offset）
        selection.anchor.offset = state.startIndex;
        selection.focus.offset = state.endIndex;

        // 2. 删除选中的文本
        selection.removeText();

        // 3. 根据触发字符创建对应的节点
        let newNode;
        if (state.triggerChar === "#") {
          const tagSuggestion = suggestion as TagSuggestion;

          if (tagSuggestion.isCreateOption) {
            // For create option, we need to create the tag asynchronously first
            // Since editor.update must return synchronously, we'll find_or_create then insert
            newNode = $createTagNode(""); // Empty tagId initially, will be filled after API call
          } else {
            // Existing tag with full tagId
            newNode = $createTagNode(tagSuggestion.tagId);
          }
        } else if (state.triggerChar === "$") {
          newNode = $createStockNode(suggestion as string, "neutral", null);
        }

        // 4. 插入节点
        if (newNode) {
          $insertNodes([newNode]);

          // 5. 在节点后插入一个空格，并移动光标
          const spaceNode = $createTextNode(" ");
          newNode.insertAfter(spaceNode);
          spaceNode.select(0, 0);
        }
      });

      // Handle async tag creation outside editor.update
      if (state.triggerChar === "#") {
        const tagSuggestion = suggestion as TagSuggestion;
        if (tagSuggestion.isCreateOption) {
          try {
            const result = await findOrCreateTagMutation.mutateAsync({
              displayName: tagSuggestion.displayName,
              colorToken: "primary",
            });

            // Immediately update the TagService cache with the new tag
            // This ensures TagNode.decorate() can find the new tag info immediately
            updateTagColorCache([result]);

            // Update the TagNode with the new tagId
            editor.update(() => {
              // Find the TagNode that was just inserted (it has empty tagId)
              // We need to traverse backward from the selection to find it
              const selection = $getSelection();
              if (!$isRangeSelection(selection)) {
                return;
              }

              const anchorNode = selection.anchor.getNode();

              // The space node should be right after the cursor, so let's look at previous nodes
              let targetTagNode: TagNode | null = null;
              let currentNode: LexicalNode | null = anchorNode;
              let checkedCount = 0;

              // Search backward through previous siblings
              while (currentNode && checkedCount < 10) {
                checkedCount++;
                if ($isTagNode(currentNode)) {
                  const tagId = currentNode.getTagId();
                  // Empty or undefined tagId means it's our newly created unfilled node
                  if (!tagId || tagId === "") {
                    targetTagNode = currentNode;
                    break;
                  }
                }
                currentNode = currentNode.getPreviousSibling();
              }

              if (targetTagNode) {
                targetTagNode.setTagId(result.tagId);
              }
            });
          } catch (error) {
            console.error("Failed to find or create tag:", error);
          }
        }
      }

      hideSuggestions();
    },
    [editor, hideSuggestions, findOrCreateTagMutation],
  );

  const updateSuggestionsPosition = useCallback(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    // Use getSelection for DOM-based position calculation
    const nativeSelection = window.getSelection();
    if (nativeSelection && nativeSelection.rangeCount > 0) {
      const range = nativeSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setSuggestionState((prev) => {
        if (!prev.triggerChar) return prev;

        return {
          ...prev,
          position: {
            left: rect.left,
            top: rect.bottom + 4,
          },
        };
      });
    }
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    // Register update listener to show/hide suggestions
    const removeUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          hideSuggestions();
          return;
        }

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          hideSuggestions();
          return;
        }

        const content = anchorNode.getTextContent();
        const offset = selection.anchor.offset;
        const beforeCursor = content.slice(0, offset);

        // Check for triggers - 支持中文字符
        for (const trigger of TRIGGERS) {
          if ((trigger.char === "#" && !tagSuggestionsEnabled) || (trigger.char === "$" && !tickerSuggestionsEnabled)) {
            continue;
          }

          // Match trigger char + content (支持中文、字母、数字、连字符、下划线)
          // 使用更宽松的匹配：任何非空白字符都匹配
          const regex = new RegExp(`\\${trigger.char}([^\\s]{0,20})$`);
          const match = beforeCursor.match(regex);

          if (match) {
            const query = match[1];
            if (query.length >= trigger.minLength) {
              setSuggestionState({
                triggerChar: trigger.char,
                query,
                startIndex: offset - match[0].length,
                endIndex: offset,
                position: null, // Will be calculated after render
                selectedIndex: 0,
              });

              // Schedule position update after render
              requestAnimationFrame(updateSuggestionsPosition);
              return;
            }
          }
        }

        // No trigger found, hide suggestions
        hideSuggestions();
      });
    });

    return () => {
      removeUpdateListener();
    };
  }, [editor, tagSuggestionsEnabled, tickerSuggestionsEnabled, hideSuggestions, updateSuggestionsPosition]);

  // Keyboard navigation
  useEffect(() => {
    // 使用 ref 获取最新的状态
    const state = suggestionStateRef.current;
    if (!state.triggerChar || !state.position) {
      return;
    }

    const suggestions = getSuggestions(state.triggerChar, state.query);
    if (suggestions.length === 0) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // 只处理特定的键 - 不要阻止其他按键
      if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === "Escape") {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          event.stopPropagation();
          setSuggestionState((prev) => ({
            ...prev,
            selectedIndex: Math.min(prev.selectedIndex + 1, suggestions.length - 1),
          }));
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          event.stopPropagation();
          setSuggestionState((prev) => ({
            ...prev,
            selectedIndex: Math.max(prev.selectedIndex - 1, 0),
          }));
        } else if (event.key === "Enter") {
          // 检查是否有建议面板打开
          const currentState = suggestionStateRef.current;
          if (currentState.triggerChar) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            const selectedSuggestion = suggestions[currentState.selectedIndex];
            if (selectedSuggestion) {
              applySuggestion(selectedSuggestion);
            }
            return; // Early return to not hide suggestions for Enter
          }
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          hideSuggestions();
        }
      }
      // Don't prevent default or stop propagation for other keys (including Cmd+Delete)
    };

    window.addEventListener("keydown", handleKeyDown, true); // Use capture phase
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
    // 依赖 suggestionState.triggerChar 和 suggestionState.position，当建议面板显示/隐藏时重新注册事件
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, hideSuggestions, getSuggestions, applySuggestion, suggestionState.triggerChar, suggestionState.position]);

  // Scroll selected item into view
  useEffect(() => {
    if (!suggestionsContainerRef.current) return;

    const container = suggestionsContainerRef.current;
    const selectedElement = container.children[0]?.children[suggestionState.selectedIndex] as HTMLElement;

    if (selectedElement) {
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const elementTop = selectedElement.offsetTop;
      const elementHeight = selectedElement.clientHeight;

      // Scroll if element is above visible area
      if (elementTop < scrollTop) {
        container.scrollTop = elementTop;
      }
      // Scroll if element is below visible area
      else if (elementTop + elementHeight > scrollTop + containerHeight) {
        container.scrollTop = elementTop + elementHeight - containerHeight;
      }
    }
  }, [suggestionState.selectedIndex]);

  // Render suggestions popup
  if (!suggestionState.triggerChar || !suggestionState.position) {
    return null;
  }

  const suggestions = getSuggestions(suggestionState.triggerChar, suggestionState.query);
  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div
      ref={suggestionsContainerRef}
      className="fixed z-50 bg-popover text-popover-foreground shadow-md rounded-md border popovers-pointer-events-auto"
      style={{
        left: `${suggestionState.position.left}px`,
        top: `${suggestionState.position.top}px`,
        maxHeight: "200px",
        overflowY: "auto",
        minWidth: "150px",
        overscrollBehavior: "none",
        scrollbarWidth: "thin",
      }}
    >
      <div className="p-1">
        {suggestions.slice(0, 10).map((suggestion, index) => {
          const tagSuggestion = suggestion as TagSuggestion;
          const key = tagSuggestion.isCreateOption
            ? `create-${tagSuggestion.displayName}`
            : tagSuggestion.tagId || tagSuggestion.displayName;

          return (
            <div
              key={key}
              className={cn(
                "px-2 py-1.5 rounded-sm text-sm cursor-pointer flex items-center gap-2 select-none",
                "hover:bg-accent hover:text-accent-foreground",
                index === suggestionState.selectedIndex && "bg-accent text-accent-foreground",
              )}
              onMouseEnter={() => {
                setSuggestionState((prev) => ({
                  ...prev,
                  selectedIndex: index,
                }));
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                applySuggestion(suggestion);
              }}
            >
              {suggestionState.triggerChar === "#" && (
                <>
                  {tagSuggestion.isCreateOption ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>➕</span>
                      <span>Create new tag</span>
                      <span className={cn(getTagColorClass(tagSuggestion.displayName))}>#{tagSuggestion.displayName}</span>
                    </div>
                  ) : (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded mr-2 ${getTagColorClass(tagSuggestion.displayName)}`}>
                      <span className="text-muted-foreground mr-0.5">#</span>
                      <span>{tagSuggestion.displayName}</span>
                    </span>
                  )}
                </>
              )}
              {suggestionState.triggerChar === "$" && (
                <>
                  <span className="text-muted-foreground mr-0.5">$</span>
                  <span>{suggestion as string}</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LexicalSuggestionsPlugin;
